import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Sparkles, Loader2, Copy, Bookmark, BookmarkCheck, 
  PenTool, Video, Zap, Camera, FileText, ChevronRight, RefreshCw,
  Crown, LogOut, User, RotateCcw, Clapperboard, Lightbulb,
  Link as LinkIcon, ExternalLink, AlertCircle, CheckCircle2, CreditCard, Users
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SubscriptionBanner from '@/components/SubscriptionBanner';
import VoiceoverButton from '@/components/VoiceoverButton';
import AppShell from '@/components/AppShell';
import Logo from '@/components/Logo';
import AppNav from '@/components/AppNav';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import UsageMeter from '@/components/UsageMeter';

const API = process.env.REACT_APP_BACKEND_URL;

const visualStyles = [
  { value: 'bold-energetic', label: 'Bold & Energetic', desc: 'Vibrant colors, dynamic animations' },
  { value: 'clean-professional', label: 'Clean & Professional', desc: 'Minimal, modern SaaS look' },
  { value: 'dark-premium', label: 'Dark & Premium', desc: 'Sleek, creator-focused aesthetic' },
];

const productCategories = [
  'Beauty & Skincare',
  'Fashion & Apparel',
  'Health & Wellness',
  'Home & Kitchen',
  'Electronics & Gadgets',
  'Food & Beverage',
  'Fitness & Sports',
  'Pet Products',
  'Baby & Kids',
  'Accessories',
  'Other',
];

const GeneratorPage = () => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [productInfo, setProductInfo] = useState({
    name: '',
    category: '',
    price: '',
    visualStyle: '',
    painPoints: '',
    features: '',
    scriptNote: '',
    scriptLength: '15-30',
  });
  const [generatedContent, setGeneratedContent] = useState(null);
  const [savedItems, setSavedItems] = useState([]);
  const [showSkitOption, setShowSkitOption] = useState(false);
  const [skitIdea, setSkitIdea] = useState(null);
  const [isGeneratingSkit, setIsGeneratingSkit] = useState(false);
  const [filmingIdeas, setFilmingIdeas] = useState(null);
  const [isGeneratingFilming, setIsGeneratingFilming] = useState(false);
  const [showExtrasPrompt, setShowExtrasPrompt] = useState(false);
  // Narrator style for the generation. Applies to hooks + script for
  // coherence. Instagram platform isn't wired here — that lives inside
  // Folders where scenes make sense.
  const [scriptStyle, setScriptStyle] = useState('ugc');
  const [isRespinningText, setIsRespinningText] = useState(false);
  const [isRespinningVerbal, setIsRespinningVerbal] = useState(false);
  const [isRespinningScript, setIsRespinningScript] = useState(false);
  const [region, setRegion] = useState('uk');
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // URL Import state
  const [showUrlImport, setShowUrlImport] = useState(false);
  const [productUrl, setProductUrl] = useState('');
  const [isScrapingUrl, setIsScrapingUrl] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  const [scrapeError, setScrapeError] = useState(null);

  // Bump this after every generation so the usage meter refetches its data
  // without us having to lift state up.
  const [usageRefreshKey, setUsageRefreshKey] = useState(0);
  const refreshUsage = () => setUsageRefreshKey((k) => k + 1);

  // Inspiration from a viral TikTok (Apify)
  const [showInspiration, setShowInspiration] = useState(false);
  const [inspirationUrl, setInspirationUrl] = useState('');
  const [isFetchingInspiration, setIsFetchingInspiration] = useState(false);
  const [inspirationTranscript, setInspirationTranscript] = useState('');
  const [inspirationError, setInspirationError] = useState(null);

  const fetchInspiration = async () => {
    setInspirationError(null);
    const url = inspirationUrl.trim();
    if (!url) {
      setInspirationError('Paste a TikTok URL first');
      return;
    }
    setIsFetchingInspiration(true);
    try {
      const res = await axios.post(
        `${API}/api/inspiration/tiktok`,
        { url },
        { headers: getAuthHeader() }
      );
      setInspirationTranscript(res.data.transcript || '');
      toast.success('Got it — viral inspiration locked in 🎯');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not pull the transcript';
      setInspirationError(msg);
      setInspirationTranscript('');
    } finally {
      setIsFetchingInspiration(false);
    }
  };

  const clearInspiration = () => {
    setInspirationTranscript('');
    setInspirationUrl('');
    setInspirationError(null);
  };

  // Handoff from the homepage free transcriber: if a visitor transcribed a
  // viral video and signed up, pre-load it here as viral inspiration.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('poettik_pending_inspiration');
      if (raw) {
        const pending = JSON.parse(raw);
        if (pending?.transcript) {
          setInspirationTranscript(pending.transcript);
          if (pending.url) setInspirationUrl(pending.url);
          toast.success('Your transcribed video is loaded as viral inspiration 🎯');
        }
        localStorage.removeItem('poettik_pending_inspiration');
      }
    } catch {
      localStorage.removeItem('poettik_pending_inspiration');
    }
  }, []);
  
  const { user, isPro, logout, getAuthHeader, isSubscriptionActive, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Email verification is no longer required to use the generator — the
  // verification redirect was removed Feb 2026. The flow now is:
  // register → straight into the generator with the trial active.

  // Check if user is VIP (bypassing paywall)
  const isVipMode = false;

  // Fetch default region
  useEffect(() => {
    axios.get(`${API}/api/settings/region`).then(res => {
      setRegion(res.data.region || 'uk');
    }).catch(() => {});
  }, []);

  // Cycling loading messages
  const loadingMessages = [
    'Researching product angles...',
    'Crafting scroll-stopping hooks...',
    'Checking viral concepts...',
    'Writing your script...',
    'Finding the perfect tone...',
    'Generating filming ideas...',
    'Polishing your content...',
    'Almost there...',
  ];

  useEffect(() => {
    if (!isLoading) { setLoadingMessage(''); return; }
    let i = 0;
    setLoadingMessage(loadingMessages[0]);
    const interval = setInterval(() => {
      i = (i + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[i]);
    }, 2000);
    return () => clearInterval(interval);
  }, [isLoading]);

  const handleInputChange = (field, value) => {
    setProductInfo(prev => ({ ...prev, [field]: value }));
  };

  // Scrape product URL
  const handleScrapeUrl = async () => {
    if (!productUrl.trim()) {
      toast.error('Please enter a TikTok Shop URL');
      return;
    }
    
    setIsScrapingUrl(true);
    setScrapeError(null);
    setScrapedData(null);
    
    try {
      const headers = isVipMode ? { 'X-VIP-Access': 'tmmb-vip' } : getAuthHeader();
      const response = await axios.post(`${API}/api/scrape-product`, {
        url: productUrl.trim()
      }, { headers });
      
      if (response.data.success) {
        setScrapedData(response.data);
        toast.success('Product data extracted! Review and edit below.');
      } else {
        setScrapeError(response.data.error_message || 'Could not extract product data');
        toast.error(response.data.error_message || 'Failed to extract product data');
      }
    } catch (error) {
      console.error('Error scraping URL:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to scrape URL. Please try filling in manually.';
      setScrapeError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsScrapingUrl(false);
    }
  };

  // Parse pasted text using LLM
  const handleParseText = async () => {
    if (!productUrl.trim()) {
      toast.error('Please paste product details');
      return;
    }
    
    setIsScrapingUrl(true);
    setScrapeError(null);
    setScrapedData(null);
    
    try {
      const headers = isVipMode ? { 'X-VIP-Access': 'tmmb-vip' } : getAuthHeader();
      const response = await axios.post(`${API}/api/parse-product-text`, {
        text: productUrl.trim()
      }, { headers });
      
      if (response.data.success) {
        setScrapedData(response.data);
        toast.success('Product details extracted! Review below.');
      } else {
        setScrapeError(response.data.error_message || 'Could not extract product data');
        toast.error(response.data.error_message || 'Failed to extract product data');
      }
    } catch (error) {
      console.error('Error parsing text:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to parse text. Please try filling in manually.';
      setScrapeError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsScrapingUrl(false);
    }
  };

  // Apply scraped data to form
  const applyScrapedData = () => {
    if (!scrapedData) return;
    
    // Build pain points from features and description
    let painPoints = '';
    if (scrapedData.features && scrapedData.features.length > 0) {
      painPoints = scrapedData.features.slice(0, 3).join(', ');
    }
    
    // Try to determine category from product name/description
    let suggestedCategory = '';
    const textToCheck = `${scrapedData.product_name || ''} ${scrapedData.description || ''}`.toLowerCase();
    
    const categoryKeywords = {
      'Beauty & Skincare': ['serum', 'cream', 'moistur', 'skin', 'beauty', 'makeup', 'cosmetic', 'glow', 'face', 'acne'],
      'Fashion & Apparel': ['dress', 'shirt', 'pants', 'jacket', 'clothing', 'wear', 'fashion', 'outfit'],
      'Health & Wellness': ['health', 'vitamin', 'supplement', 'wellness', 'organic', 'natural'],
      'Home & Kitchen': ['home', 'kitchen', 'cook', 'clean', 'household', 'organiz'],
      'Electronics & Gadgets': ['phone', 'electronic', 'gadget', 'charger', 'bluetooth', 'wireless', 'led', 'smart'],
      'Food & Beverage': ['food', 'drink', 'snack', 'beverage', 'coffee', 'tea'],
      'Fitness & Sports': ['fitness', 'gym', 'workout', 'exercise', 'sport', 'yoga'],
      'Pet Products': ['pet', 'dog', 'cat', 'animal'],
      'Baby & Kids': ['baby', 'kid', 'child', 'toy', 'toddler'],
      'Accessories': ['bag', 'wallet', 'watch', 'jewelry', 'accessory', 'necklace', 'bracelet'],
    };
    
    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some(kw => textToCheck.includes(kw))) {
        suggestedCategory = category;
        break;
      }
    }
    
    setProductInfo({
      name: scrapedData.product_name || '',
      category: suggestedCategory || '',
      price: scrapedData.price || '',
      visualStyle: '',
      painPoints: painPoints,
    });
    
    setShowUrlImport(false);
    setScrapedData(null);
    setProductUrl('');
    toast.success('Product details applied! Review and adjust as needed.');
  };

  // Reset URL import state
  const resetUrlImport = () => {
    setShowUrlImport(false);
    setProductUrl('');
    setScrapedData(null);
    setScrapeError(null);
  };

  // Re-spin hooks function for VIP users
  const respinHooks = async (hookType) => {
    if (!isVipMode) return;
    
    const setLoading = hookType === 'text' ? setIsRespinningText : setIsRespinningVerbal;
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/respin-hooks`, {
        product_name: productInfo.name,
        category: productInfo.category,
        price: productInfo.price,
        pain_points: productInfo.painPoints,
        features: productInfo.features,
        hook_type: hookType,
      }, {
        headers: { 'X-VIP-Access': 'tmmb-vip' }
      });
      
      if (hookType === 'text') {
        setGeneratedContent(prev => ({
          ...prev,
          text_hooks: response.data.hooks
        }));
        toast.success('Poettik spun up fresh text hooks! 🎰');
      } else {
        setGeneratedContent(prev => ({
          ...prev,
          verbal_hooks: response.data.hooks
        }));
        toast.success('Poettik spun up fresh verbal hooks! 🎰');
      }
    } catch (error) {
      console.error('Error respinning hooks:', error);
      toast.error('Couldn\'t re-spin right now. Try again!');
    } finally {
      setLoading(false);
    }
  };

  const respinScript = async () => {
    setIsRespinningScript(true);
    try {
      const headers = isVipMode ? { 'X-VIP-Access': 'tmmb-vip' } : getAuthHeader();
      const response = await axios.post(`${API}/api/respin-script`, {
        product_name: productInfo.name,
        category: productInfo.category,
        price: productInfo.price,
        visual_style: productInfo.visualStyle,
        pain_points: productInfo.painPoints,
        features: productInfo.features,
        script_note: productInfo.scriptNote,
        script_length: productInfo.scriptLength,
        vip_mode: isVipMode,
        region: region,
      }, { headers });
      setGeneratedContent(prev => ({ ...prev, script: response.data.script }));
      toast.success('Fresh script generated!');
      if (!isVipMode) refreshUsage();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || 'Monthly limit reached.');
      } else {
        toast.error('Failed to regenerate script.');
      }
    } finally { setIsRespinningScript(false); }
  };


  const generateContent = async () => {
    if (!productInfo.name || !productInfo.category || !productInfo.price) {
      toast.error('Please fill in all product details');
      return;
    }

    setIsLoading(true);
    try {
      const headers = isVipMode ? { 'X-VIP-Access': 'tmmb-vip' } : getAuthHeader();
      const response = await axios.post(`${API}/api/generate-content`, {
        product_name: productInfo.name,
        category: productInfo.category,
        price: productInfo.price,
        visual_style: productInfo.visualStyle,
        pain_points: productInfo.painPoints,
        features: productInfo.features,
        script_note: productInfo.scriptNote,
        script_length: productInfo.scriptLength,
        vip_mode: isVipMode,
        region: region,
        platform: 'tiktok',
        script_style: scriptStyle,
        inspiration_transcript: inspirationTranscript || null,
        inspiration_url: inspirationTranscript ? inspirationUrl : null,
      }, {
        headers
      });
      
      setGeneratedContent(response.data);
      setStep(2);
      setShowExtrasPrompt(true);
      toast.success('Poettik has crafted your content ideas! 🎯');
      window.dispatchEvent(new Event('usage-updated'));
      if (!isVipMode) refreshUsage();
    } catch (error) {
      console.error('Error generating content:', error);
      if (error.response?.status === 403) {
        toast.error('Your trial has expired. Please upgrade to Pro.');
        navigate('/pricing');
      } else if (error.response?.status === 429) {
        toast.error(error.response.data.detail || 'Monthly limit reached. Upgrade for more uses!');
      } else {
        toast.error('Poettik couldn\'t generate content right now. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generateSkit = async () => {
    setIsGeneratingSkit(true);
    try {
      const headers = isVipMode ? { 'X-VIP-Access': 'tmmb-vip' } : getAuthHeader();
      const response = await axios.post(`${API}/api/generate-skit`, {
        product_name: productInfo.name,
        category: productInfo.category,
        price: productInfo.price,
        visual_style: productInfo.visualStyle,
        pain_points: productInfo.painPoints,
        features: productInfo.features,
        vip_mode: isVipMode,
        region: region,
      }, { headers });
      setSkitIdea(response.data.skit_idea);
      toast.success('Poettik has created your skit idea! 🎬');
      if (!isVipMode) refreshUsage();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || 'Monthly limit reached.');
      } else {
        toast.error('Couldn\'t create the skit right now. Please try again.');
      }
    } finally { setIsGeneratingSkit(false); }
  };

  const generateFilmingIdeas = async () => {
    setIsGeneratingFilming(true);
    try {
      const headers = isVipMode ? { 'X-VIP-Access': 'tmmb-vip' } : getAuthHeader();
      const response = await axios.post(`${API}/api/generate-filming-ideas`, {
        product_name: productInfo.name,
        category: productInfo.category,
        price: productInfo.price,
        visual_style: productInfo.visualStyle,
        pain_points: productInfo.painPoints,
        features: productInfo.features,
        vip_mode: isVipMode,
        region: region,
      }, { headers });
      setFilmingIdeas(response.data.filming_ideas);
      toast.success('Filming ideas ready! 🎥');
      if (!isVipMode) refreshUsage();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || 'Monthly limit reached.');
      } else {
        toast.error('Couldn\'t generate filming ideas right now. Please try again.');
      }
    } finally { setIsGeneratingFilming(false); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const toggleSaveItem = (type, index, content) => {
    const itemId = `${type}-${index}`;
    const existingIndex = savedItems.findIndex(item => item.id === itemId);
    
    if (existingIndex > -1) {
      setSavedItems(prev => prev.filter(item => item.id !== itemId));
      toast.info('Removed from saved items');
    } else {
      const newItem = {
        id: itemId,
        type,
        content,
        productName: productInfo.name,
        savedAt: new Date().toISOString(),
      };
      setSavedItems(prev => [...prev, newItem]);
      
      // Save to localStorage
      const existing = JSON.parse(localStorage.getItem('kylo_saved_ideas') || '[]');
      localStorage.setItem('kylo_saved_ideas', JSON.stringify([...existing, newItem]));
      toast.success('Saved to your collection!');
    }
  };

  const isItemSaved = (type, index) => {
    return savedItems.some(item => item.id === `${type}-${index}`);
  };

  const resetGenerator = () => {
    setStep(1);
    setProductInfo({ name: '', category: '', price: '', visualStyle: '', painPoints: '', features: '', scriptNote: '', scriptLength: '15-30' });
    setGeneratedContent(null);
    setSkitIdea(null);
    setFilmingIdeas(null);
    setShowExtrasPrompt(false);
    setShowSkitOption(false);
    setSavedItems([]);
    resetUrlImport();
  };

  const renderContentItem = (item, index, type, title) => (
    <div key={index} className="group relative p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-all duration-300">
      <p className="text-sm text-foreground pr-20 leading-relaxed">{item}</p>
      <div className="absolute right-3 top-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto z-10">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 bg-muted/80 hover:bg-muted"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(item, title);
          }}
        >
          <Copy className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 bg-muted/80 hover:bg-muted"
          onClick={(e) => {
            e.stopPropagation();
            toggleSaveItem(type, index, item);
          }}
        >
          {isItemSaved(type, index) ? (
            <BookmarkCheck className="w-4 h-4 text-primary" />
          ) : (
            <Bookmark className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );

  // Special render for visual hooks with "Why This Works"
  const renderVisualHookItem = (item, index) => (
    <div key={index} className="group relative p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-all duration-300">
      <p className="text-sm text-foreground pr-20 leading-relaxed font-normal">{item.hook}</p>
      <div className="mt-2 pt-2 border-t border-border/30">
        <p className="text-xs text-muted-foreground">
          <span className="text-primary font-normal">Why this works:</span> {item.why_it_works}
        </p>
      </div>
      <div className="absolute right-3 top-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto z-10">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 bg-muted/80 hover:bg-muted"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(item.hook, 'Visual Hook');
          }}
        >
          <Copy className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 bg-muted/80 hover:bg-muted"
          onClick={(e) => {
            e.stopPropagation();
            toggleSaveItem('visual-hook', index, item.hook);
          }}
        >
          {isItemSaved('visual-hook', index) ? (
            <BookmarkCheck className="w-4 h-4 text-primary" />
          ) : (
            <Bookmark className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );

  // Render card with optional re-spin for VIP users
  const renderContentCardWithRespin = (title, icon, items, type, hookType, isRespinning) => (
    <Card variant="glass" className="h-full flex flex-col">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary">
              {icon}
            </div>
            <CardTitle className="text-lg">{title}</CardTitle>
          </div>
          {isPro && (
            <button
              onClick={() => respinHooks(hookType)}
              disabled={isRespinning}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
            >
              {isRespinning ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RotateCcw className="w-3 h-3" />
              )}
              Re-spin
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-3">
          {items?.map((item, index) => renderContentItem(item, index, type, title))}
        </div>
      </CardContent>
    </Card>
  );

  const renderContentCard = (title, icon, items, type) => (
    <Card variant="glass" className="h-full flex flex-col">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary">
            {icon}
          </div>
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-3">
          {items?.map((item, index) => renderContentItem(item, index, type, title))}
        </div>
      </CardContent>
    </Card>
  );

  const renderVisualHooksCard = (title, icon, items) => (
    <Card variant="glass" className="h-full flex flex-col">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary">
            {icon}
          </div>
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="space-y-3">
          {items?.map((item, index) => renderVisualHookItem(item, index))}
        </div>
      </CardContent>
    </Card>
  );

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <AppShell title="New script">
      <SEO title="Content Generator" description="Generate viral hooks, 30-second scripts and filming ideas for any TikTok Shop product in seconds." noindex />
      <SubscriptionBanner />

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Monthly usage meter — hidden for VIPs and admins */}
        <UsageMeter
          authHeader={getAuthHeader}
          isAuthenticated={isAuthenticated}
          refreshKey={usageRefreshKey}
        />

        {/* Step 1: Product Input */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto animate-fade-in-up">
            <div className="text-center mb-10">
              <h1 className="font-display text-3xl sm:text-4xl font-bold mb-4">
                {isVipMode ? (
                  <>Tell <span className="gradient-text">Poettik</span> About Your Product</>
                ) : (
                  <>Enter Your <span className="gradient-text">Product Details</span></>
                )}
              </h1>
              <p className="text-muted-foreground">
                {isVipMode 
                  ? "Welcome VIP! Let Poettik craft some viral content ideas for you"
                  : "Tell us about your product and we'll generate viral content ideas"
                }
              </p>
            </div>

            {/* URL Import Section - HIDDEN: TikTok CAPTCHA blocks scraping */}
            {false && (isVipMode || isPro) && (
              <Card variant="gradient" className="p-6 mb-6 animate-fade-in-up" data-testid="url-import-section">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary shrink-0">
                    <LinkIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">Import from TikTok Shop URL</h3>
                      <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full">
                        {isVipMode ? 'VIP' : 'Pro'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Paste a TikTok Shop product URL and Poettik will extract the details
                    </p>
                    
                    {!showUrlImport ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowUrlImport(true)}
                        data-testid="show-url-import-btn"
                        className="relative z-10"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Import from URL
                      </Button>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex gap-2">
                          <Input
                            placeholder="https://www.tiktok.com/..."
                            value={productUrl}
                            onChange={(e) => setProductUrl(e.target.value)}
                            className="flex-1"
                            data-testid="product-url-input"
                          />
                          <Button
                            variant="premium"
                            onClick={handleScrapeUrl}
                            disabled={isScrapingUrl || !productUrl.trim()}
                            data-testid="scrape-url-btn"
                          >
                            {isScrapingUrl ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Extract'
                            )}
                          </Button>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={resetUrlImport}
                          >
                            Cancel
                          </Button>
                        </div>
                        
                        {scrapeError && (
                          <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg" data-testid="scrape-error">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            <span>{scrapeError}</span>
                          </div>
                        )}
                        
                        {scrapedData && scrapedData.success && (
                          <div className="bg-muted/50 rounded-lg p-4 space-y-3" data-testid="scraped-data-preview">
                            <div className="flex items-center gap-2 text-sm text-primary mb-2">
                              <CheckCircle2 className="w-4 h-4" />
                              <span className="font-medium">Product data extracted!</span>
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              {scrapedData.product_name && (
                                <div>
                                  <span className="text-muted-foreground">Name:</span>{' '}
                                  <span className="font-medium">{scrapedData.product_name}</span>
                                </div>
                              )}
                              {scrapedData.price && (
                                <div>
                                  <span className="text-muted-foreground">Price:</span>{' '}
                                  <span className="font-medium">{scrapedData.price}</span>
                                </div>
                              )}
                              {scrapedData.description && (
                                <div>
                                  <span className="text-muted-foreground">Description:</span>{' '}
                                  <span className="text-xs">{scrapedData.description.substring(0, 150)}...</span>
                                </div>
                              )}
                              {scrapedData.features && scrapedData.features.length > 0 && (
                                <div>
                                  <span className="text-muted-foreground">Features found:</span>{' '}
                                  <span className="text-xs">{scrapedData.features.length} items</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex gap-2 pt-2">
                              <Button
                                variant="premium"
                                size="sm"
                                onClick={applyScrapedData}
                                data-testid="apply-scraped-data-btn"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Use This Data
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={resetUrlImport}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            <Card variant="glass" className="p-8">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Glow Serum Pro, LED Face Mask"
                    value={productInfo.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    data-testid="product-name-input"
                  />
                </div>

                {/* Viral TikTok inspiration — always visible, sits under Product Name */}
                <div
                  className="rounded-xl border border-border/60 bg-card/30 p-4 space-y-2"
                  data-testid="inspiration-panel"
                >
                  <div className="flex items-center justify-between gap-2">
                    <Label className="flex items-center gap-2 text-sm">
                      📺 Viral inspiration <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    {inspirationTranscript && (
                      <button
                        type="button"
                        onClick={clearInspiration}
                        className="text-xs text-muted-foreground hover:text-foreground"
                        data-testid="inspiration-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {!inspirationTranscript ? (
                    <>
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://www.tiktok.com/@creator/video/123..."
                          value={inspirationUrl}
                          onChange={(e) => setInspirationUrl(e.target.value)}
                          disabled={isFetchingInspiration}
                          data-testid="inspiration-url-input"
                        />
                        <Button
                          type="button"
                          onClick={fetchInspiration}
                          disabled={isFetchingInspiration || !inspirationUrl.trim()}
                          variant="secondary"
                          data-testid="inspiration-fetch"
                        >
                          {isFetchingInspiration ? 'Pulling…' : 'Fetch'}
                        </Button>
                      </div>
                      {inspirationError && (
                        <p className="text-xs text-rose-400" data-testid="inspiration-error">
                          {inspirationError}
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        Paste a viral TikTok URL — we'll grab its transcript and use it to mirror the
                        pacing, structure and tone for your product. Works best on videos with spoken audio.
                      </p>
                    </>
                  ) : (
                    <div
                      className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5 text-xs text-emerald-200"
                      data-testid="inspiration-locked"
                    >
                      <p className="font-medium text-emerald-300 mb-1">
                        🎯 Inspiration locked in
                      </p>
                      <p className="text-muted-foreground line-clamp-3 leading-snug">
                        “{inspirationTranscript.slice(0, 200)}{inspirationTranscript.length > 200 ? '…' : ''}”
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Product Category</Label>
                  <Select
                    value={productInfo.category}
                    onValueChange={(value) => handleInputChange('category', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {productCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Price Point</Label>
                  <Input
                    id="price"
                    placeholder="e.g., £29.99, Under £50"
                    value={productInfo.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="painPoints" className="flex items-center gap-2">
                    Pain Points <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="painPoints"
                    placeholder="e.g., dry skin, breakouts... or leave blank and Poettik will find them"
                    value={productInfo.painPoints}
                    onChange={(e) => handleInputChange('painPoints', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Tell Poettik what problems this product solves, or leave it empty and I&apos;ll research it for you!
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="features" className="flex items-center gap-2">
                    Features & Benefits <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="features"
                    placeholder="e.g., organic ingredients, fast results, easy to use..."
                    value={productInfo.features}
                    onChange={(e) => handleInputChange('features', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Key selling points that make this product special
                  </p>
                </div>

                {productInfo.name && (
                  <div className="space-y-2 animate-fade-in-up">
                    <Label htmlFor="style">Visual Style Preference</Label>
                    <Select
                      value={productInfo.visualStyle}
                      onValueChange={(value) => handleInputChange('visualStyle', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose your content vibe" />
                      </SelectTrigger>
                      <SelectContent>
                        {visualStyles.map((style) => (
                          <SelectItem key={style.value} value={style.value}>
                            <div className="flex flex-col">
                              <span>{style.label}</span>
                              <span className="text-xs text-muted-foreground">{style.desc}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Script Note */}
                <div className="space-y-2">
                  <Label htmlFor="scriptNote" className="flex items-center gap-2">
                    Script Direction <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="scriptNote"
                    placeholder="e.g., Use an 'I'm sending it back' scenario, or start with a question..."
                    value={productInfo.scriptNote}
                    onChange={(e) => handleInputChange('scriptNote', e.target.value)}
                    data-testid="script-note-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Describe how you want the script to be written
                  </p>
                </div>

                {/* Script Length */}
                <div className="space-y-2">
                  <Label>Script Length</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: '0-15', label: '0–15s' },
                      { value: '15-30', label: '15–30s' },
                      { value: '30-45', label: '30–45s' },
                      { value: '45-60', label: '45s–1m' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleInputChange('scriptLength', opt.value)}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${productInfo.scriptLength === opt.value ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
                        data-testid={`script-length-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Region Toggle */}
                <div className="mt-4 flex items-center justify-center gap-2">
                  <span className="text-xs text-muted-foreground">Content style:</span>
                  <div className="flex rounded-lg border border-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setRegion('uk')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${region === 'uk' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid="region-toggle-uk"
                    >
                      🇬🇧 UK
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegion('usa')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${region === 'usa' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid="region-toggle-usa"
                    >
                      🇺🇸 USA
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-border bg-background/40 p-3">
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
                      Script style
                    </p>
                    <div className="inline-flex rounded-full border border-border bg-background/40 p-0.5 text-xs" data-testid="gen-script-style-switch">
                      {[
                        { slug: 'ugc', label: 'UGC creator' },
                        { slug: 'authority', label: 'Authority' },
                        { slug: 'negative', label: 'Negative' },
                      ].map((s) => (
                        <button
                          key={s.slug}
                          type="button"
                          onClick={() => setScriptStyle(s.slug)}
                          className={
                            'px-3 py-1.5 rounded-full transition font-medium ' +
                            (scriptStyle === s.slug
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground')
                          }
                          data-testid={`gen-script-style-${s.slug}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground sm:max-w-[16rem] sm:text-right">
                    {scriptStyle === 'ugc' && 'Casual first-person creator voice.'}
                    {scriptStyle === 'authority' && 'Expert giving a tip — calm, contrarian, mechanism-led.'}
                    {scriptStyle === 'negative' && 'Sceptical review that ends on "way cheaper on TikTok than in the shop".'}
                  </p>
                </div>

                <Button
                  variant="premium"
                  size="lg"
                  className="w-full mt-4"
                  onClick={generateContent}
                  disabled={isLoading || !productInfo.name || !productInfo.category || !productInfo.price}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {loadingMessage}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Let Poettik Generate Ideas
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Step 2: Generated Content */}
        {step === 2 && generatedContent && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-4">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm text-muted-foreground">Content generated for</span>
                <span className="text-sm font-medium text-foreground">{productInfo.name}</span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold">
                Your <span className="gradient-text">Content</span> Ideas
              </h1>
            </div>

            {/* Inspiration recap — shown when the user pulled a viral TikTok */}
            {inspirationTranscript && (
              <div
                className="max-w-3xl mx-auto mb-8 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4"
                data-testid="inspiration-recap"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base">📺</span>
                    <span className="text-sm font-semibold text-emerald-300">
                      Modelled on a viral TikTok
                    </span>
                  </div>
                  {inspirationUrl && (
                    <a
                      href={inspirationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-300 hover:text-emerald-200 underline-offset-4 hover:underline shrink-0"
                      data-testid="inspiration-recap-link"
                    >
                      Watch original ↗
                    </a>
                  )}
                </div>
                <p className="text-xs text-muted-foreground leading-snug mb-2">
                  We used the pacing, structure and tone from this video to shape your script.
                </p>
                <details className="group [&_summary::-webkit-details-marker]:hidden">
                  <summary
                    className="flex items-center justify-between gap-2 cursor-pointer text-xs font-medium text-emerald-300 hover:text-emerald-200 py-1.5 select-none"
                    data-testid="inspiration-recap-toggle"
                  >
                    <span>See original transcribed script</span>
                    <span className="text-sm leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <div className="pt-2">
                    <blockquote
                      className="text-sm text-foreground/90 leading-relaxed border-l-2 border-emerald-500/40 pl-3 italic whitespace-pre-wrap"
                      data-testid="inspiration-recap-transcript"
                    >
                      {inspirationTranscript}
                    </blockquote>
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={() => copyToClipboard(inspirationTranscript, 'Inspiration transcript')}
                        className="text-[11px] text-muted-foreground hover:text-foreground"
                        data-testid="inspiration-recap-copy"
                      >
                        Copy transcript
                      </button>
                    </div>
                  </div>
                </details>
              </div>
            )}

            {/* 30-Second Script (part of main generation) — shown FIRST, ahead
                of the hook grid so creators see the finished script first. */}
            {generatedContent.script && (
              <Card variant="glass" className="p-8 mb-8 animate-fade-in-up">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/20 to-secondary/20 flex items-center justify-center text-accent">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-medium">
                        {productInfo.scriptLength === '0-15' ? '15-Second' : productInfo.scriptLength === '30-45' ? '45-Second' : productInfo.scriptLength === '45-60' ? '60-Second' : '30-Second'} Script
                      </h3>
                      <p className="text-xs text-muted-foreground">Ready to film</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {isPro && (
                      <Button variant="ghost" size="sm" onClick={respinScript} disabled={isRespinningScript}>
                        {isRespinningScript ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        Re-spin
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(generatedContent.script, 'Script')}>
                      <Copy className="w-4 h-4 mr-2" /> Copy
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleSaveItem('script', 0, generatedContent.script)}>
                      {isItemSaved('script', 0) ? <><BookmarkCheck className="w-4 h-4 mr-2 text-primary" /> Saved</> : <><Bookmark className="w-4 h-4 mr-2" /> Save</>}
                    </Button>
                  </div>
                </div>
                <div className="p-6 rounded-xl bg-muted/30">
                  <pre className="whitespace-pre-wrap text-sm text-foreground leading-relaxed font-sans font-light">{generatedContent.script}</pre>
                </div>
                <VoiceoverButton scriptText={generatedContent.script} testidPrefix="gen-voiceover" />
              </Card>
            )}

            {/* Hooks Grid — text, visual, verbal */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {renderContentCardWithRespin(
                "Text Hooks",
                <PenTool className="w-5 h-5" />,
                generatedContent.text_hooks,
                "text-hook",
                "text",
                isRespinningText
              )}
              {renderVisualHooksCard(
                "Visual Opening Hooks",
                <Video className="w-5 h-5" />,
                generatedContent.visual_hooks
              )}
              {renderContentCardWithRespin(
                "Verbal Hooks",
                <Zap className="w-5 h-5" />,
                generatedContent.verbal_hooks,
                "verbal-hook",
                "verbal",
                isRespinningVerbal
              )}
            </div>

            {/* Extras Prompt - Filming Ideas & Skit */}
            {showExtrasPrompt && (
              <Card variant="gradient" className="p-8 text-center mb-8 animate-fade-in-up">
                <h3 className="font-display text-xl font-semibold mb-3">
                  Want More from <span className="gradient-text">Poettik</span>?
                </h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  Generate additional content ideas for your product
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="premium"
                    size="lg"
                    onClick={() => { generateFilmingIdeas(); }}
                    disabled={isGeneratingFilming || !!filmingIdeas}
                  >
                    {isGeneratingFilming ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating...</>
                    ) : filmingIdeas ? (
                      <><Camera className="w-5 h-5 mr-2" /> Filming Ideas Generated</>
                    ) : (
                      <><Camera className="w-5 h-5 mr-2" /> Filming Ideas</>
                    )}
                  </Button>
                  <Button
                    variant="premium"
                    size="lg"
                    onClick={() => { generateSkit(); }}
                    disabled={isGeneratingSkit || !!skitIdea}
                  >
                    {isGeneratingSkit ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating...</>
                    ) : skitIdea ? (
                      <><Clapperboard className="w-5 h-5 mr-2" /> Skit Created</>
                    ) : (
                      <><Clapperboard className="w-5 h-5 mr-2" /> Skit Idea</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setShowExtrasPrompt(false)}
                  >
                    No Thanks
                  </Button>
                </div>
              </Card>
            )}

            {/* Generated Filming Ideas */}
            {filmingIdeas && (
              <div className="mb-8">
                {renderContentCard(
                  "Filming Content Ideas",
                  <Camera className="w-5 h-5" />,
                  filmingIdeas,
                  "filming-idea"
                )}
              </div>
            )}

            {/* Generated Skit */}
            {skitIdea && (
              <Card variant="glass" className="p-6 mb-8 animate-fade-in-up">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent/20 to-secondary/20 flex items-center justify-center text-accent shrink-0">
                    <Clapperboard className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-display font-semibold text-lg">Skit Idea</h3>
                      <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full">Creative</span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{skitIdea}</p>
                    <div className="mt-3 flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(skitIdea, 'Skit Idea')}>
                        <Copy className="w-4 h-4 mr-2" /> Copy
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleSaveItem('skit-idea', 0, skitIdea)}>
                        {isItemSaved('skit-idea', 0) ? <><BookmarkCheck className="w-4 h-4 mr-2 text-primary" /> Saved</> : <><Bookmark className="w-4 h-4 mr-2" /> Save</>}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
      </main>
    </AppShell>
  );
};

export default GeneratorPage;
